/**
 * The walkthrough: one video and the few sentences that frame it.
 *
 * Also the body of the welcome overlay, so a visitor who dismisses it on the
 * first day can find exactly the same thing from the menu on the second.
 */
import { VIDEO_ID, videoUrl } from "../demo";
import { Icon } from "../components/primitives";

/**
 * The video itself, or an honest stand-in for it.
 *
 * There is no video yet. A dark rectangle with a dead play button would read as
 * something broken, so the placeholder says what it is and what the page is for
 * - and disappears of its own accord the moment an id is configured.
 */
export function VideoFrame({ autoplay = false }: { autoplay?: boolean }) {
  if (!VIDEO_ID) {
    return (
      <div className="videoframe videoframe--empty">
        <Icon name="movie" size={28} className="u-muted" />
        <div className="videoframe__note">
          The walkthrough video is being recorded. Until it lands, the shortest way in is to open
          a drawing from the library and ask the assistant about it.
        </div>
      </div>
    );
  }
  return (
    <div className="videoframe">
      <iframe
        src={videoUrl(VIDEO_ID, autoplay)}
        title="How to use the Technical Drawings Reader demo"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

const STEPS = [
  {
    icon: "description",
    title: "Open a drawing",
    text: "The library holds prepared sheets. Opening one shows the page beside everything that was read off it, drawing by drawing.",
  },
  {
    icon: "crop_free",
    title: "Check a value against the sheet",
    text: "Every reading belongs to a marked region. Point at a row to light up its box on the drawing, or at a box to find the row.",
  },
  {
    icon: "forum",
    title: "Ask about what you are looking at",
    text: "The assistant answers from the drawing you have open - the selector above the box says which documents a question will be answered from.",
  },
  {
    icon: "compare_arrows",
    title: "Compare two revisions",
    text: "Tick two sheets in the library. Values lines up what was extracted and tints the differences; Visual puts the sheets side by side.",
  },
];

export function HowTo({ onContact }: { onContact: () => void }) {
  return (
    <section className="screen">
      <div className="screen__head" style={{ display: "block" }}>
        <h1 className="screen__title" style={{ marginBottom: 4 }}>
          How to use it
        </h1>
        <div className="screen__caption">
          A short tour of what this demo does, and what the full product does with your own
          drawings.
        </div>
      </div>

      <div className="screen__body screen__body--scroll">
        <div className="prose">
          <VideoFrame />

          <div className="steps">
            {STEPS.map((step) => (
              <div className="step" key={step.title}>
                <Icon name={step.icon} size={20} className="step__icon" />
                <div>
                  <div className="step__title">{step.title}</div>
                  <div className="step__text">{step.text}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="prose__cta">
            <div>
              <div className="step__title">Want to see it read your own drawings?</div>
              <div className="step__text">
                The demo runs on a fixed, prepared library. Analyzing your sheets - your formats,
                your title blocks, your tables - is what the full solution does.
              </div>
            </div>
            <button className="promobar__cta promobar__cta--solid" onClick={onContact}>
              Contact us
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
